import React, { useMemo } from 'react';
import { EditableComponent } from '@adobe/aem-react-editable-components';

import { OTabContainer as OTabContainerComp } from '@marriott/mi-web-ui-kit';
import clsx from 'clsx';

export const OTabContainerConfig = {
  emptyLabel: 'OTabContainer',
  isEmpty: () => true,
  resourceType: `${process.env['NEXT_PUBLIC_AEM_SITE']}/components/content/o-tab-container`,
};

// AEM `MapTo` wrapper for the shared `OTabContainer` molecule.
//
// Bridges the AEM `:items.itemN` sibling-map shape into the molecule's
// expected `mTabGroup.mTabItemMulti[i].mSlot.cqItems` shape. No-op when
// there is no sibling `:items` to enrich (raw mocks, empty pages).
//
// Runtime gotcha: `mTabGroup` and `cqItems` live on `props.model`, not on
// `props` directly. See `./README.md` for the full contract and pitfalls.

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getFromAnywhere(props: any, ...keys: string[]) {
  for (const k of keys) {
    if (props?.[k] !== undefined) return props[k];
    if (props?.model?.[k] !== undefined) return props.model[k];
  }
  return undefined;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function enrichTabItemsWithSlots(props: any) {
  const mTabGroup = getFromAnywhere(props, 'mTabGroup');
  const tabItems = mTabGroup?.mTabItemMulti;
  const containerItems = getFromAnywhere(props, 'cqItems', ':items');
  const pagePath = getFromAnywhere(props, 'pagePath');
  const itemPath = getFromAnywhere(props, 'itemPath');

  if (!Array.isArray(tabItems) || !containerItems || typeof containerItems !== 'object') {
    return props;
  }

  let didEnrich = false;
  const enrichedTabItems = tabItems.map(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (tab: any) => {
      const nodeName = tab?.nodeName;
      const sibling = nodeName ? containerItems[nodeName] : undefined;
      if (!sibling || typeof sibling !== 'object') return tab;

      const existingMSlot = tab?.mSlot ?? {};
      const hasAuthoredCqItems = existingMSlot.cqItems && Object.keys(existingMSlot.cqItems).length > 0;
      if (hasAuthoredCqItems) return tab;

      const cqItems = sibling[':items'] ?? sibling.cqItems ?? {};
      const order = sibling[':itemsOrder'] ?? sibling.cqItemsOrder ?? Object.keys(cqItems);

      didEnrich = true;
      return {
        ...tab,
        mSlot: {
          ...existingMSlot,
          cqItems,
          model: { ...(existingMSlot.model ?? {}), ':order': order },
          pagePath: existingMSlot.pagePath ?? pagePath,
          itemPath: existingMSlot.itemPath ?? (itemPath ? `${itemPath}/${nodeName}` : ''),
          aemReactComponentMapping: existingMSlot.aemReactComponentMapping ?? props.aemReactComponentMapping,
        },
      };
    }
  );

  if (!didEnrich) return props;

  // Write the enriched mTabGroup back to both `props.mTabGroup` and
  // `props.model.mTabGroup` so the molecule sees it regardless of which
  // side it destructures from.
  const nextModel = props.model ? { ...props.model } : undefined;
  if (nextModel?.mTabGroup) {
    nextModel.mTabGroup = { ...nextModel.mTabGroup, mTabItemMulti: enrichedTabItems };
  }

  return {
    ...props,
    ...(nextModel ? { model: nextModel } : {}),
    mTabGroup: {
      ...(props.mTabGroup ?? mTabGroup),
      mTabItemMulti: enrichedTabItems,
    },
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const OTabContainer = (props: any) => {
  const model = props.model || {};
  const { styleclass, componentId } = model;

  const enrichedProps = useMemo(() => enrichTabItemsWithSlots(props), [props]);

  return (
    <div
      data-testid="o-tab-container-comp"
      data-component-name="o-tab-container"
      id={componentId}
      className={clsx(styleclass)}
    >
      <EditableComponent config={OTabContainerConfig} {...enrichedProps}>
        <OTabContainerComp {...enrichedProps} />
      </EditableComponent>
    </div>
  );
};
